/*
 * Search
 * @module components
 * @flow
 */

import React, { Children, Fragment, isValidElement, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { palette } from 'styled-theme';
import { FormattedMessage } from 'react-intl';
import { getTime } from 'date-fns';
import { Query } from 'react-apollo';
import * as R from 'ramda';

import { Tabs } from '~/components/Tabs';
import { Block } from '~/components/Block';
import { DatePicker } from '~/components/DatePicker';
import { Button, StyledButton } from '~/components/Button';
import { ClickAwayListener } from '~/components/ClickAwayListener';

import { SETTINGS } from '~/gql';
// $FlowIssue
import { ReactComponent as IconFilters } from '@/icons/filters.svg';
// $FlowIssue
import { ReactComponent as IconClose } from '@/icons/ic-clear.svg';

import { SearchFilter } from './components/SearchFilter';

import messages from './messages';

export type SearchProps = {
  children?: React$Node,
  note?: string,
  tabs?: Array<*>,
  values?: {|
    [key: string]: *,
  |},
  isVisible?: boolean,
  rightPositioned?: boolean,
  // $FlowIssue
  calendarData?: ?Object,
  onShow?: () => void,
  onHide?: () => void,
  onChange: (
    string,
    | 'numeric'
    | 'date'
    | 'select'
    | 'search'
    | 'calendar'
    | 'input'
    | 'checkbox',
    string | number,
  ) => void,
  onFocus?: (SyntheticEvent<HTMLInputElement>) => void,
  onClear?: () => void,
  onSubmit?: () => void,
  device?: $PropertyType<DeviceQuery, 'device'>,
  orientation?: $PropertyType<DeviceQuery, 'orientation'>,
};

const SearchWrapper = styled.div.attrs(() => ({
  testid: 'search',
}))`
  position: relative;

  & > div[role='input'] {
    margin: 0 15px;
    padding-top: 30px;
    width: calc(100% - 30px);
    z-index: 12;
  }
`;

const SearchTabs = styled(Tabs)`
  height: 48px;
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
  box-shadow: 0 2px 16px 1px rgba(176, 192, 237, 0.42);
  justify-content: center;

  ${ifProp(
    { device: 'mobile' },
    css`
      @media (max-width: 350px) {
        height: 60px;
      }
    `,
  )}
`;

const SearchTabItem = styled(Tabs.Item)`
  ${ifProp(
    { device: 'mobile' },
    css`
      @media (max-width: 350px) {
        display: inline-block;
        margin-right: 20px;
        text-align: center;
      }
    `,
    css`
      margin-right: 40px;

      &:last-child {
        margin-right: 0;
      }
    `,
  )}
`;

const SearchView = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 71px;
  height: 32px;
  margin: 30px 0 15px;
  padding: 0 14px 0 24px;
  border-radius: 30px;
  border: solid 1px ${palette('primary', 1)};
  color: ${palette('primary', 1)};
  cursor: ${ifProp('disabled', 'default', 'pointer')};
`;

const SearchCloseButton = styled(IconClose)`
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 10;
  cursor: pointer;
`;

const SearchContainer = styled(Block)`
  box-sizing: border-box;
  padding-bottom: 10px;
  /* NOTE: modal, search and tooltip must have the same z-index value but lower than notifications container */
  z-index: 999;
  overflow-y: auto;

  ${ifProp(
    'isTabbed',
    css`
      display: block;
    `,
  )};

  ${ifProp(
    'isPortrait',
    css`
      position: fixed;
      left: 8px;
      top: 8px;
      right: 8px;
      max-height: calc(100% - 15px);
    `,
    css`
      position: absolute;
      top: 0;
      right: ${ifProp('rightPositioned', 0, 'auto')};
    `,
  )};
`;

const SearchFilters = styled.div.attrs(() => ({
  className: 'react-search-filters',
}))`
  ${ifProp(
    { device: 'desktop' },
    css`
      padding: 32px 32px 23px;
      width: 493px;
    `,
    css`
      padding: 32px 25px 18px;
      width: ${ifProp({ orientation: 'portrait' }, '100%', '360px')};
      height: 100%;
      box-sizing: border-box;
      max-height: 100%;
    `,
  )}

  ${/* sc-selector */ DatePicker} {
    ${ifProp(
      { device: 'desktop' },
      css`
        margin-right: 20px;
      `,
      css`
        margin-bottom: 20px;
        padding: 0;
        width: 100%;
      `,
    )};
  }
`;

const SearchFiltersContainer = styled.div.attrs(() => ({
  className: 'react-search-filters-container',
}))`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SearchControls = styled.div`
  display: flex;
  margin-top: 30px;
  align-items: center;
  justify-content: flex-end;
`;

const SearchControlsButtons = styled.div`
  ${/* sc-selector */ StyledButton} + ${/* sc-selector */ StyledButton} {
    margin-left: 17px;
  }
`;

const SearchClearButton = styled(Button)`
  ${ifProp(
    { device: 'mobile' },
    css`
      @media (max-width: 350px) {
        min-width: 110px;
      }
    `,
  )};
`;

const SearchSubmitButton = styled(Button)`
  ${ifProp(
    { device: 'mobile' },
    css`
      @media (max-width: 350px) {
        min-width: 110px;
      }
    `,
    css`
      margin-left: 30px !important;
    `,
  )};
`;

const SearchNote = styled.span`
  display: block;
  box-sizing: border-box;
  padding: 0 32px 10px 32px;
  color: #b9c0c9;
  font-size: 12px;
  text-align: ${ifProp({ device: 'desktop' }, 'right', 'center')};
`;

/**
 * Search React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @param {Array} [props.note] - search note
 * @param {Array} [props.tabs] - tabs
 * @param {Object} [props.values] - list of values
 * @param {Object} [props.calendarData] - data for calendar
 * @param {boolean} [props.isVisible] - visible state of search popup
 * @param {boolean} [props.rightPositioned] - show dropdown from with start position right 0
 * @param {Function} [props.onShow] - on main search field focus callback
 * @param {Function} [props.onHide] - on main search field blur callback
 * @param {Function} [props.onToggleExtra] - on "Show more / Show less" button callback
 * @param {Function} [props.onChange] - on change fields callback
 * @param {Function} [props.onFocus] - on focus fields callback
 * @param {Function} [props.onClear] - on "Clear" button callback
 * @param {Function} [props.onSubmit] - on "Submit" button callback
 * @example <caption>Simple usage</caption>
 * <Search>Text</Search>
 * @returns {React.Node<*>} React-component
 */
export const Search = ({
  children,
  note,
  tabs,
  values,
  calendarData,
  validationError,
  isVisible,
  rightPositioned,
  onShow,
  onHide,
  onToggleExtra,
  onChange,
  onFocus,
  onClear,
  onSubmit,
  device,
  orientation,
  ...rest
}: SearchProps) => {
  const isTabbed = !R.isEmpty(tabs);
  const filterValues = isTabbed ? R.omit(['searchType'], values) : values;
  const isFiltered = !!R.join('', R.values(filterValues));
  const isPortrait = device !== 'desktop' && orientation === 'portrait';
  const activeTab = isTabbed ? R.find(R.propEq('active', true), tabs) : {};
  const searchNote = isTabbed ? activeTab.note : note;

  const mapPropsToFilters = filters => {
    const searchFilters =
      // $FlowIssue
      filters.type === Fragment ? filters.props.children : filters;

    return Children.map(searchFilters, child =>
      isValidElement(child)
        ? cloneElement(child, {
            onChange,
            onFocus,
            device,
            value: R.pipe(
              R.path(['props', 'name']),
              // eslint-disable-next-line no-underscore-dangle
              R.prop(R.__, filterValues),
            )(child),
          })
        : null,
    );
  };

  const searchTabs = R.map(
    ({ icon, title, active, onClick }) => (
      <SearchTabItem
        icon={icon}
        title={title}
        active={active}
        onClick={onClick}
        device={device}
      >
        {title}
      </SearchTabItem>
    ),
    tabs,
  );

  const searchFilters = filters => (
    <SearchFiltersContainer>
      {mapPropsToFilters(filters)}
      <SearchControls>
        <SearchControlsButtons>
          {isFiltered && (
            <SearchClearButton small outlined onClick={onClear} device={device}>
              <FormattedMessage {...messages.clear} />
            </SearchClearButton>
          )}
          <SearchSubmitButton small onClick={onSubmit} device={device}>
            <FormattedMessage {...messages.apply} />
          </SearchSubmitButton>
        </SearchControlsButtons>
      </SearchControls>
    </SearchFiltersContainer>
  );

  let calendarComponent = !R.isEmpty(calendarData) && (
    <Query query={SETTINGS}>
      {({ data }) => {
        const { key: calendarKey, value: calendarValue, ...calendarProps } =
          calendarData || {};

        const locale = R.pathOr('en', ['settings', 'locale'], data);

        return (
          <DatePicker
            locale={locale}
            value={calendarValue}
            onChange={value =>
              onChange(calendarKey, 'calendar', getTime(value))
            }
            {...calendarProps}
          />
        );
      }}
    </Query>
  );

  if (calendarComponent && device !== 'desktop') {
    calendarComponent = createPortal(
      calendarComponent,
      // $FlowIssue
      document.getElementById(calendarData.key).closest('.react-search-filter'),
    );
  }

  return (
    <SearchWrapper {...rest}>
      <SearchView onClick={onShow}>
        <FormattedMessage {...messages.filter} />
        <IconFilters />
      </SearchView>
      {isVisible && (
        <ClickAwayListener onClickAway={onHide}>
          <SearchContainer
            isTabbed={isTabbed}
            isPortrait={isPortrait}
            rightPositioned={rightPositioned}
          >
            <SearchCloseButton onClick={onHide} />
            {isTabbed && <SearchTabs device={device}>{searchTabs}</SearchTabs>}
            {!R.isEmpty(calendarData) && calendarComponent}
            <SearchFilters device={device} orientation={orientation}>
              {isTabbed
                ? searchFilters(activeTab.filters)
                : searchFilters(children)}
            </SearchFilters>
            {searchNote && (
              <SearchNote device={device}>{searchNote}</SearchNote>
            )}
          </SearchContainer>
        </ClickAwayListener>
      )}
    </SearchWrapper>
  );
};

Search.defaultProps = {
  onShow: () => {},
  onHide: () => {},
  onChange: () => () => {},
  onFocus: () => {},
  onClear: () => {},
  onSubmit: () => {},
  tabs: [],
  values: {},
  calendarData: {},
};

Search.Filter = SearchFilter;
